'use client';

import type { CSSProperties, FC } from 'react';
import { SkinImage } from '../../components/ui/SkinImage';
import type { ContractItem } from '../../types/contracts';
import type { CratePhase } from './useCrateRoll';

const seededRand = (n: number) => {
  const x = Math.sin(n * 99.13) * 43758.5453;
  return x - Math.floor(x);
};
const getScatter = (index: number) => ({
  cx: 30 + seededRand(index * 2 + 1) * 28,
  cy: 44 + seededRand(index * 2 + 2) * 20,
  rot: (seededRand(index * 3 + 3) - 0.5) * 64,
});

interface CrateResultItem {
  name: string;
  imageUrl: string;
  rarityColor?: string;
}

interface CrateProps {
  items: ContractItem[];
  phase: CratePhase;
  resultItem?: CrateResultItem | null;
  locked: boolean;
  onRemoveItem: (itemId: string) => void;
}

export const Crate: FC<CrateProps> = ({ items, phase, resultItem, locked, onRemoveItem }) => {
  const itemsHidden = phase === 'rolling' || phase === 'opening' || phase === 'reveal';
  const showCrateResult = phase === 'opening' || phase === 'reveal';
  const lidStyle: CSSProperties =
    phase === 'closing'
      ? { animation: 'crateLidCover 1150ms cubic-bezier(0.4,0,0.2,1) forwards' }
      : phase === 'rolling'
        ? { opacity: 1, transform: 'translateY(0)' }
        : phase === 'opening'
          ? { animation: 'crateLidUncover 1150ms cubic-bezier(0.4,0,0.2,1) forwards' }
          : { opacity: 0, transform: 'translateY(-16%)' };

  return (
    <div
      className="relative mx-auto mt-4"
      style={{ width: '100%', maxWidth: 420, aspectRatio: '979 / 704' }}
    >

      <img
        src="/crate-empty.webp"
        alt=""
        aria-hidden
        className="absolute inset-0 w-full h-full object-contain pointer-events-none select-none"
      />


      {items.map((item, index) => {
        const s = getScatter(index);
        return (
          <div
            key={item.id}
            className="absolute cursor-pointer"
            style={{
              left: `${s.cx}%`,
              top: `${s.cy}%`,
              width: '54%',
              aspectRatio: '1 / 1',
              transform: `translate(-50%,-50%) rotate(${s.rot}deg)`,
              zIndex: 10 + index,
              opacity: itemsHidden ? 0 : 1,
              transition: 'opacity 180ms ease-out',
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (!locked) onRemoveItem(item.id);
            }}
          >
            <SkinImage
              name={item.item.name}
              src={item.item.imageUrl}
              imgClassName="w-full h-full object-contain"
              imgStyle={{ filter: `drop-shadow(0 2px 6px rgba(0,0,0,0.7)) drop-shadow(0 0 8px ${item.item.rarityColor}aa)` }}
            />
          </div>
        );
      })}


      {resultItem && (
        <div
          className="absolute inset-0 flex items-center justify-center pointer-events-none"
          style={{ zIndex: 30, opacity: showCrateResult ? 1 : 0, transition: 'opacity 250ms 120ms' }}
        >
          <SkinImage
            name={resultItem.name}
            src={resultItem.imageUrl}
            style={{ width: '78%', height: '78%' }}
            imgClassName="w-full h-full object-contain"
            imgStyle={{
              transform: showCrateResult ? 'scale(1)' : 'scale(0.6)',
              transition: 'transform 360ms 120ms cubic-bezier(0.34,1.56,0.64,1)',
              filter: `drop-shadow(0 4px 16px ${resultItem.rarityColor || '#3b82f6'})`,
            }}
          />
        </div>
      )}


      <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 40 }}>
        <img
          src="/crate-lid.webp"
          alt=""
          aria-hidden
          className="absolute inset-0 w-full h-full object-contain select-none"
          style={{
            ...lidStyle,
            filter: 'drop-shadow(0 10px 22px rgba(0,0,0,0.6))',
          }}
        />
      </div>
    </div>
  );
};
